import {Choice, showNotification} from 'platform/components';
import {Heading, Show, Text, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {isNotEmpty} from 'ramda';

import {
  useClearDefaultEmailCredentialsMutation,
  useListEmailCredentialsQuery,
  useSetDefaultEmailCredentialsMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {getMailboxOptionsByOwner, handleApiError} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

export function DefaultMailboxSection(props: TestIdProps) {
  const {data: emailCredentials, isLoading: isLoadingMailboxes} = useListEmailCredentialsQuery({});
  const [setDefaultMailbox, setDefaultMailboxStatus] = useSetDefaultEmailCredentialsMutation();
  const [clearDefaultMailbox, clearDefaultMailboxStatus] =
    useClearDefaultEmailCredentialsMutation();

  const mailboxes = emailCredentials?.credentials ?? [];
  const defaultMailboxId = mailboxes.find((mailbox) => mailbox.isDefault)?.id ?? null;
  const mailboxOptions = getMailboxOptionsByOwner(mailboxes).filter((group) =>
    isNotEmpty(group.options)
  );

  const isSaving = setDefaultMailboxStatus.isLoading || clearDefaultMailboxStatus.isLoading;

  const handleChange = (mailboxId: string | Nullish) => {
    const request = match(mailboxId)
      .with(Pattern.nullish, () => clearDefaultMailbox())
      .otherwise((id) =>
        setDefaultMailbox({setDefaultEmailCredentialsRequestBody: {emailCredentialsId: id}})
      );

    request
      .unwrap()
      .then(() =>
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'))
      )
      .catch(handleApiError);
  };

  return (
    <Show when={isNotEmpty(mailboxes)}>
      <VStack spacing={2}>
        <Heading size={4}>{i18n.t('page.myProfile.labels.defaultMailbox')}</Heading>
        <Text size="small" color="tertiary">
          {i18n.t('page.myProfile.labels.defaultMailboxDescription')}
        </Text>
        <Choice
          value={defaultMailboxId}
          onChange={handleChange}
          options={mailboxOptions}
          isLoading={isLoadingMailboxes || isSaving}
          isDisabled={isSaving}
          placeholder={i18n.t('general.labels.selectOption')}
          data-testid={suffixTestId('defaultMailbox', props)}
        />
      </VStack>
    </Show>
  );
}
